// Boton "Refresh" dividido: a la izquierda refresca ahora; a la derecha abre un
// panel para activar el refresco automatico y elegir cada cuanto.
//
// La eleccion se recuerda por navegador (localStorage). El intervalo lo aplica
// quien usa el componente, pasandole `intervalMs` a su query; TanStack Query ya
// lo pausa mientras la pestaña no esta visible.

import { useEffect, useId, useRef, useState } from 'react';
import { checkboxCls } from '@/lib/form-styles';

export const AUTO_REFRESH_OPTIONS = [
  { ms: 30_000, label: '30 s' },
  { ms: 60_000, label: '1 min' },
  { ms: 120_000, label: '2 min' },
  { ms: 300_000, label: '5 min' },
  { ms: 600_000, label: '10 min' },
  { ms: 900_000, label: '15 min' },
] as const;

const DEFAULT_MS = 60_000;

export interface AutoRefresh {
  enabled: boolean;
  ms: number;
}

function isValidMs(ms: unknown): ms is number {
  return AUTO_REFRESH_OPTIONS.some((o) => o.ms === ms);
}

function readStored(key: string): AutoRefresh {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const v = JSON.parse(raw) as Partial<AutoRefresh>;
      return { enabled: v.enabled === true, ms: isValidMs(v.ms) ? v.ms : DEFAULT_MS };
    }
  } catch {
    // localStorage indisponible o valor corrupto: arranca apagado.
  }
  return { enabled: false, ms: DEFAULT_MS };
}

/** Estado del refresco automatico, recordado en este navegador bajo `storageKey`. */
export function useAutoRefresh(storageKey: string): [AutoRefresh, (next: AutoRefresh) => void] {
  const [value, setValue] = useState<AutoRefresh>(() => readStored(storageKey));
  function update(next: AutoRefresh): void {
    setValue(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ignore: queda solo en memoria
    }
  }
  return [value, update];
}

export function intervalLabel(ms: number): string {
  return AUTO_REFRESH_OPTIONS.find((o) => o.ms === ms)?.label ?? `${Math.round(ms / 1000)} s`;
}

const SEGMENT =
  'min-h-[2.75rem] text-sm hover:bg-surface-muted dark:hover:bg-surface-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-light disabled:opacity-50 disabled:cursor-not-allowed';

export default function RefreshControl({
  onRefresh,
  fetching,
  disabled,
  auto,
  onAutoChange,
}: {
  onRefresh: () => void;
  fetching: boolean;
  disabled: boolean;
  auto: AutoRefresh;
  onAutoChange: (next: AutoRefresh) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const groupName = useId();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <div className="flex rounded-lg border border-surface-border dark:border-surface-dark-border overflow-hidden">
        <button
          type="button"
          onClick={onRefresh}
          disabled={disabled || fetching}
          className={`${SEGMENT} flex items-center gap-2 px-4 py-2`}
        >
          <span aria-hidden="true" className={fetching ? 'inline-block animate-spin' : 'inline-block'}>
            ↻
          </span>
          <span>{fetching ? 'Actualizando…' : 'Refresh'}</span>
          {auto.enabled ? (
            <span className="flex items-center gap-1 text-xs text-status-success">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-status-success" />
              cada {intervalLabel(auto.ms)}
            </span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label="Opciones de refresco automático"
          className={`${SEGMENT} px-3 border-l border-surface-border dark:border-surface-dark-border`}
        >
          <span aria-hidden="true">▾</span>
        </button>
      </div>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Refresco automático"
          className="absolute right-0 mt-2 w-60 z-30 rounded-lg border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark shadow-lg p-3 space-y-3"
        >
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={auto.enabled}
              onChange={(e) => onAutoChange({ ...auto, enabled: e.target.checked })}
              className={checkboxCls()}
            />
            Refresco automático
          </label>

          <fieldset>
            <legend className="text-xs text-ink-muted mb-1">Cada</legend>
            <div className="grid grid-cols-3 gap-1">
              {AUTO_REFRESH_OPTIONS.map((o) => {
                const selected = auto.ms === o.ms;
                return (
                  <label
                    key={o.ms}
                    className={`cursor-pointer rounded-md border px-2 py-1.5 text-center text-sm focus-within:ring-2 focus-within:ring-brand-light ${
                      selected && auto.enabled
                        ? 'border-brand bg-brand/10 text-brand dark:text-brand-light font-medium'
                        : selected
                          ? 'border-ink-subtle font-medium'
                          : 'border-surface-border dark:border-surface-dark-border hover:bg-surface-muted dark:hover:bg-surface-dark-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name={groupName}
                      value={o.ms}
                      checked={selected}
                      // Elegir un intervalo ya lo activa: no hace falta tildar aparte.
                      // onClick y no onChange: tocar el intervalo que ya estaba
                      // marcado (con el refresco apagado) no dispara change.
                      // Las flechas del teclado tambien disparan click.
                      onClick={() => onAutoChange({ enabled: true, ms: o.ms })}
                      readOnly
                      className="sr-only"
                    />
                    {o.label}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <p className="text-xs text-ink-muted">
            Se pausa mientras la pestaña no está a la vista.
          </p>
        </div>
      ) : null}
    </div>
  );
}
